import React, { useEffect, useRef } from 'react'

import type { ProductGroup, ProductOption } from '../types/catalog'
import { billingTypeFor } from '../utils/billingType'
import { formatCents } from '../utils/formatCents'

// Backend returns `unit: 'yr'` (no leading slash); mock seed uses '/yr'.
// Normalise so the dropdown shows "$1,100/yr" either way.
function formatPriceWithUnit(priceCents: number | undefined, unit: string | undefined): string {
  const price = formatCents(priceCents)
  if (!unit) return price
  return `${price}${unit.startsWith('/') ? unit : '/' + unit}`
}

export function findProductItem(
  groups: ProductGroup[],
  value: string,
): ProductOption | null {
  for (const group of groups) {
    for (const item of group.items) {
      if (item.value === value) return item
    }
  }
  return null
}

interface ProductPickerProps {
  value: string
  onChange: (value: string) => void
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  products: ProductGroup[]
}

const BILLING_SECTIONS: Array<{ mode: 'annual' | 'monthly' | 'otp'; title: string }> = [
  { mode: 'annual', title: 'Annual Subscription' },
  { mode: 'monthly', title: 'Monthly Subscription' },
  { mode: 'otp', title: 'One-Time Purchase' },
]

export const ProductPicker: React.FunctionComponent<ProductPickerProps> = (props) => {
  const { value, onChange, isOpen, onOpenChange, products } = props
  const ref = useRef<HTMLDivElement | null>(null)
  const selected = value ? findProductItem(products, value) : null

  useEffect(() => {
    if (!isOpen) return undefined
    const handler = (event: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(event.target as Node)) onOpenChange(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, onOpenChange])

  return (
    <div
      ref={ref}
      className={`product-picker${isOpen ? ' open' : ''}`}
      onClick={(event) => {
        event.stopPropagation()
        onOpenChange(!isOpen)
      }}
    >
      <span className={`picker-label${selected ? '' : ' placeholder'}`}>
        {selected ? selected.name : 'Select product...'}
      </span>
      <span className="picker-caret">▾</span>
      <div className="picker-menu" onClick={(event) => event.stopPropagation()}>
        {BILLING_SECTIONS.map(({ mode, title }) => {
          const families = products
            .map((group) => ({
              group: group.group,
              items: group.items.filter((option) => billingTypeFor(option) === mode),
            }))
            .filter((family) => family.items.length > 0)
          if (families.length === 0) return null
          return (
            <div key={mode}>
              <div className={`picker-billing-section picker-billing-section--${mode}`}>
                {title}
              </div>
              {families.map((family) => (
                <div key={family.group}>
                  <div className="picker-group">{family.group}</div>
                  {family.items.map((item) => (
                    <div
                      key={item.value}
                      className={`picker-option${value === item.value ? ' selected' : ''}`}
                      onClick={() => {
                        onChange(item.value)
                        onOpenChange(false)
                      }}
                    >
                      <span>{item.name}</span>
                      <span className="picker-price">
                        {item.priceLabel ?? formatPriceWithUnit(item.priceCents, item.unit)}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
